import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  GripVertical,
  Eye,
  EyeOff,
  Search,
  X,
  AlertCircle,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { CostCentreColumnDefinition } from './cost-centres-table';

type ColumnCategory = 'identification' | 'financial' | 'time' | 'calculated' | 'metadata';

interface CostCentresColumnConfiguratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: CostCentreColumnDefinition[];
  onSave: (columns: CostCentreColumnDefinition[]) => void;
}

export function CostCentresColumnConfigurator({
  open,
  onOpenChange,
  columns: initialColumns,
  onSave,
}: CostCentresColumnConfiguratorProps) {
  const [columns, setColumns] = useState<CostCentreColumnDefinition[]>(initialColumns);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ColumnCategory | 'all'>('all');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setColumns(initialColumns);
      setSearchTerm('');
      setCategoryFilter('all');
      setHasChanges(false);
    }
  }, [open, initialColumns]);

  // Track changes
  useEffect(() => {
    const changed = JSON.stringify(columns) !== JSON.stringify(initialColumns);
    setHasChanges(changed);
  }, [columns, initialColumns]);

  // Filter columns based on search and category
  const filteredColumns = useMemo(() => {
    return columns.filter((col) => {
      const matchesSearch =
        searchTerm === '' ||
        col.label.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || col.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [columns, searchTerm, categoryFilter]);

  // Count visible columns
  const visibleCount = columns.filter((col) => col.visible).length;
  const totalCount = columns.length;

  // Handle visibility toggle
  const handleToggleVisibility = (columnKey: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.key === columnKey ? { ...col, visible: !col.visible } : col
      )
    );
  };

  // Handle show all
  const handleShowAll = () => {
    setColumns((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  // Handle hide all
  const handleHideAll = () => {
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        visible: col.key === 'code' || col.key === 'name' ? true : false, // Keep code and name visible
      }))
    );
  };

  // Handle drag start
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  // Handle drop
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const draggedColumn = filteredColumns[draggedIndex];
    const targetColumn = filteredColumns[dropIndex];

    // Reorder in the full columns array
    const newColumns = [...columns];
    const draggedOriginalIndex = newColumns.findIndex((c) => c.key === draggedColumn.key);
    const targetOriginalIndex = newColumns.findIndex((c) => c.key === targetColumn.key);

    newColumns.splice(draggedOriginalIndex, 1);
    newColumns.splice(targetOriginalIndex, 0, draggedColumn);

    setColumns(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Handle save
  const handleSave = async () => {
    // Validate at least code and name are visible
    const codeVisible = columns.find(c => c.key === 'code')?.visible;
    const nameVisible = columns.find(c => c.key === 'name')?.visible;
    
    if (!codeVisible || !nameVisible) {
      toast.error('Code and Name columns must be visible', {
        description: 'These columns are required for identification.',
        icon: <AlertCircle className="h-4 w-4" />,
      });
      return;
    }

    setIsSaving(true);
    try {
      // Simulate save delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      onSave(columns);
      toast.success('Column configuration saved', {
        description: 'Your column preferences have been updated.',
        icon: <Check className="h-4 w-4" />,
      });
      onOpenChange(false);
    } catch {
      toast.error('Failed to save configuration', {
        description: 'Please try again or contact support if the problem persists.',
        action: {
          label: 'Retry',
          onClick: () => handleSave(),
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      onOpenChange(false);
    }
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    setShowUnsavedDialog(false);
    onOpenChange(false);
  };

  const getCategoryLabel = (category: ColumnCategory): string => {
    switch (category) {
      case 'identification': return 'Identification';
      case 'financial': return 'Financial';
      case 'time': return 'Time';
      case 'calculated': return 'Calculated';
      case 'metadata': return 'Metadata';
      default: return category;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-[700px] max-h-[90vh] flex flex-col p-0"
          onInteractOutside={(e) => {
            if (hasChanges) {
              e.preventDefault();
              setShowUnsavedDialog(true);
            }
          }}
          onEscapeKeyDown={(e) => {
            if (hasChanges) {
              e.preventDefault();
              setShowUnsavedDialog(true);
            }
          }}
        >
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>Configure Cost Centre Columns</DialogTitle>
            <DialogDescription>
              Customise which columns appear in the cost centres table and control their display order.
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Filter Controls */}
            <div className="px-6 py-4 space-y-3 border-b">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search columns..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Select
                  value={categoryFilter}
                  onValueChange={(value) => setCategoryFilter(value as ColumnCategory | 'all')}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="identification">Identification</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="time">Time</SelectItem>
                    <SelectItem value="calculated">Calculated</SelectItem>
                    <SelectItem value="metadata">Metadata</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bulk Actions */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowAll}
                    className="h-8"
                  >
                    Show All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleHideAll}
                    className="h-8"
                  >
                    Hide All
                  </Button>
                </div>
                <div className="text-sm text-muted-foreground">
                  Showing {filteredColumns.length} of {totalCount} columns
                  {visibleCount > 0 && (
                    <span className="ml-2">
                      ({visibleCount} visible)
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Column List */}
            <div className="flex-1 overflow-hidden min-h-0">
              <ScrollArea className="h-full">
                <ScrollAreaViewport className="h-full max-h-[50vh]">
                  <div className="px-6 py-4 space-y-1">
                    {filteredColumns.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No columns found</p>
                        <p className="text-xs mt-1">Try adjusting your search or filter</p>
                      </div>
                    ) : (
                      filteredColumns.map((column, index) => (
                        <div
                          key={column.key}
                          draggable={true}
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          className={cn(
                            'group flex items-center gap-3 p-3 rounded-lg border bg-card transition-all',
                            'hover:bg-accent/50 hover:border-accent',
                            draggedIndex === index && 'opacity-50',
                            dragOverIndex === index && 'border-primary border-2'
                          )}
                        >
                          {/* Drag Handle */}
                          <div className="flex-shrink-0">
                            <GripVertical
                              className={cn(
                                'h-5 w-5 text-muted-foreground cursor-grab active:cursor-grabbing',
                                'group-hover:text-foreground transition-colors'
                              )}
                              aria-label="Drag to reorder"
                            />
                          </div>

                          {/* Column Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm truncate">{column.label}</h4>
                              <Badge variant="secondary" className="text-xs">
                                {getCategoryLabel(column.category)}
                              </Badge>
                            </div>
                          </div>

                          {/* Visibility Toggle */}
                          <button
                            onClick={() => handleToggleVisibility(column.key)}
                            className={cn(
                              'flex-shrink-0 p-2 rounded-md transition-colors hover:bg-accent cursor-pointer',
                              column.visible ? 'text-primary' : 'text-muted-foreground'
                            )}
                            aria-label={column.visible ? 'Hide column' : 'Show column'}
                          >
                            {column.visible ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 py-4 border-t bg-muted/30">
            <div className="flex items-center justify-between w-full">
              <div className="text-xs text-muted-foreground">
                {hasChanges && (
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-burntorange-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardChanges} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
